import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { HistoryPanel } from '../history/HistoryPanel';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { DeleteDialog, GeneralError, issueFor, PRICING_KEY, PricingNav, SERVICES_RESOURCE } from './PricingLayout';
import { daysToMilli, milliToDays } from './units';
import { NumberInput } from '../ui/NumberInput';
import { Link } from 'react-router-dom';
import { FUNCTIONS_RESOURCE, PEOPLE_KEY } from '../people/types';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';

/** As the API sends it; `priceGrosze` is absent for a role that may not see prices. */
export type Service = {
  id: string;
  name: string;
  unitLabel: string;
  priceGrosze?: number;
  defaultDaysMilli: number;
  linear: boolean;
  discountable: boolean;
  sort: number;
  /** One of the two transports (P5): its price is the flat amount; the rates are edited under "Transport". */
  transport: 'truck' | 'car' | null;
  transportBaseGrosze?: number;
  transportPerKmGrosze?: number;
  /** The crew function the service IS (owner 2026-10-24: group „Obsada”, made with the function); absent when hidden. */
  functionId?: string | null;
  version: number;
};

const FIELDS = ['name', 'unitLabel', 'priceGrosze', 'defaultDaysMilli', 'linear', 'discountable', 'functionId'] as const;

const draftOf = (s?: Service) => ({
  name: s?.name ?? '',
  unitLabel: s?.unitLabel ?? '',
  price: s?.priceGrosze !== undefined ? plnToInput(s.priceGrosze) : '',
  days: milliToDays(s?.defaultDaysMilli ?? 1000),
  linear: s?.linear ?? true,
  discountable: s?.discountable ?? false,
});

/** Add (no `service`) or edit. Only visible fields are shown and sent; on edit only the changed ones (rule 7a). */
function ServiceForm({ service, showPrice, onClose }: { service?: Service; showPrice: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [base, setBase] = useState(service);
  const [draft, setDraft] = useState(draftOf(service));
  const [localError, setLocalError] = useState<{ price?: string; days?: string }>({});
  const set = (patch: Partial<typeof draft>) => setDraft({ ...draft, ...patch });

  const save = useMutation({
    mutationFn: (values: { priceGrosze: number | null; defaultDaysMilli: number }) => {
      const original = draftOf(base);
      const changed = (key: keyof typeof draft) => !base || draft[key] !== original[key];
      const fields = {
        ...(changed('name') ? { name: draft.name } : {}),
        ...(changed('unitLabel') ? { unitLabel: draft.unitLabel } : {}),
        ...(showPrice && values.priceGrosze !== null && (!base || values.priceGrosze !== base.priceGrosze) ? { priceGrosze: values.priceGrosze } : {}),
        ...(!base || values.defaultDaysMilli !== base.defaultDaysMilli ? { defaultDaysMilli: values.defaultDaysMilli } : {}),
        ...(changed('linear') ? { linear: draft.linear } : {}),
        ...(changed('discountable') ? { discountable: draft.discountable } : {}),
      };
      return base
        ? api<Service>(`/api/pricing/services/${base.id}`, { method: 'PATCH', body: { version: base.version, ...fields } })
        : api<Service>('/api/pricing/services', { method: 'POST', body: fields });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRICING_KEY });
      // a function's service renamed: the function is renamed too
      if (base?.functionId) await queryClient.invalidateQueries({ queryKey: PEOPLE_KEY });
      onClose();
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  function reload() {
    const current = (save.error as ApiError).current as Service | undefined;
    if (current) {
      setBase(current);
      setDraft(draftOf(current));
    }
    save.reset();
    void queryClient.invalidateQueries({ queryKey: PRICING_KEY });
  }

  /** What to send, or null with the reasons shown. */
  function values(): { priceGrosze: number | null; defaultDaysMilli: number } | null {
    const errors: { price?: string; days?: string } = {};
    let priceGrosze: number | null = null;
    if (showPrice) {
      if (draft.price.trim() === '') errors.price = 'Podaj cenę w złotych, np. 1 500,00';
      else {
        try {
          priceGrosze = parsePln(draft.price);
        } catch (err) {
          errors.price = (err as Error).message;
        }
      }
    }
    const defaultDaysMilli = daysToMilli(draft.days);
    if (defaultDaysMilli === null) errors.days = 'Podaj liczbę dni od 0,5, np. 1 albo 1,5';
    setLocalError(errors);
    if (errors.price || errors.days || defaultDaysMilli === null) return null;
    return { priceGrosze, defaultDaysMilli };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const v = values();
    if (v) save.mutate(v);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(draft, draftOf(base)),
    save: async (el) => {
      const v = values();
      if (!v || !validForm(el)) return false;
      await save.mutateAsync(v);
      return true;
    },
  });

  const err = (path: string) => issueFor(save.error, path);
  return (
    <form ref={guardRef} className="panel form" aria-label={base ? `Edycja: ${base.name}` : 'Nowa usługa'} data-feature-id="pricing.services" onSubmit={submit}>
      {!base && <h2>Nowa usługa</h2>}
      {conflict && <ConflictNotice onReload={reload} />}
      <GeneralError error={save.error} fields={FIELDS} />
      <label className="field">
        <span>Nazwa</span>
        <input value={draft.name} onChange={(e) => set({ name: e.target.value })} required autoComplete="off" />
        {base?.functionId && <span className="hint">To usługa funkcji z obsady: nowa nazwa zmieni też funkcję w „Osoby → Funkcje”.</span>}
        {err('name') && <span className="field-error">{err('name')}</span>}
      </label>
      {showPrice && (
        <label className="field">
          <span>Cena (zł)</span>
          <NumberInput kind="money" value={draft.price} onValueChange={(v) => set({ price: v })} placeholder="np. 1 500,00" />
          {(localError.price ?? err('priceGrosze')) && <span className="field-error">{localError.price ?? err('priceGrosze')}</span>}
        </label>
      )}
      <label className="field">
        <span>Jednostka</span>
        <input value={draft.unitLabel} onChange={(e) => set({ unitLabel: e.target.value })} placeholder="np. kpl., os." autoComplete="off" />
        {err('unitLabel') && <span className="field-error">{err('unitLabel')}</span>}
      </label>
      <label className="field">
        <span>Domyślnie dni</span>
        <NumberInput value={draft.days} onValueChange={(v) => set({ days: v })} />
        {(localError.days ?? err('defaultDaysMilli')) && <span className="field-error">{localError.days ?? err('defaultDaysMilli')}</span>}
      </label>
      <label className="check">
        <input type="checkbox" checked={draft.linear} onChange={(e) => set({ linear: e.target.checked })} />
        <span>Liniowo (każdy dzień 100%, bez schematu dni)</span>
      </label>
      <label className="check">
        <input type="checkbox" checked={draft.discountable} onChange={(e) => set({ discountable: e.target.checked })} />
        <span>Objęta rabatem stałego klienta</span>
      </label>
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj usługę'}
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

const yesNo = (v: boolean) => (v ? 'tak' : 'nie');

type TableProps = { label: string; services: Service[]; me: NonNullable<ReturnType<typeof useMe>['me']>; showPrice: boolean; showFunction: boolean; crew: boolean; editingId: string | null; setEditingId: (id: string | null) => void; setDeleting: (s: Service) => void; historyId: string | null; setHistoryId: (id: string | null) => void };

/** One group of the price list: „Obsada” (the functions' services: no delete here) or the other services. */
function ServicesTable({ label, services, me, showPrice, showFunction, crew, editingId, setEditingId, setDeleting, historyId, setHistoryId }: TableProps) {
  const seeHistory = can(me, 'history.entries', 'view');
  const columns = showPrice ? 6 : 5;
  return (
    <div className="table-wrap">
      <table className="table pricing-table" aria-label={label}>
        <thead>
          <tr>
            <th scope="col">Nazwa</th>
            {showPrice && (
              <th scope="col" className="num">
                Cena
              </th>
            )}
            <th scope="col" className="num hide-phone">
              Domyślnie dni
            </th>
            <th scope="col" className="hide-phone">
              Liniowo
            </th>
            <th scope="col" className="hide-phone">
              Objęta rabatem
            </th>
            <th scope="col" className="tpl-actions-col">
              <span className="visually-hidden">Działania</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {services.map((s) => {
            const more = [
              ...(seeHistory ? [{ label: historyId === s.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === s.id ? null : s.id) }] : []),
              // the two transports stay (P5); a function's service goes only with its function (owner 2026-10-24) — a role
              // that does not see the link cannot tell it, so it gets no „Usuń” at all (review: the server would refuse)
              ...(can(me, SERVICES_RESOURCE, 'delete') && !s.transport && !crew && showFunction && !s.functionId ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(s) }] : []),
            ];
            return (
              <Fragment key={s.id}>
                <tr>
                  <td>
                    <strong>{s.name}</strong>
                    {s.unitLabel && <span className="muted"> · {s.unitLabel}</span>}
                    {s.transport && <span className="muted"> · cena = ryczałt do progu; powyżej progu wg km (stawki w zakładce „Transport”)</span>}
                    {crew && showPrice && s.priceGrosze === 0 && <span className="muted"> · ustaw cenę</span>}
                  </td>
                  {showPrice && <td className="num">{s.priceGrosze !== undefined ? formatPln(s.priceGrosze) : ''}</td>}
                  <td className="num hide-phone">{milliToDays(s.defaultDaysMilli)}</td>
                  <td className="hide-phone">{yesNo(s.linear)}</td>
                  <td className="hide-phone">{yesNo(s.discountable)}</td>
                  <td className="tpl-actions">
                    {can(me, SERVICES_RESOURCE, 'edit') && editingId !== s.id && (
                      <button className="btn small" type="button" aria-label={`Edytuj: ${s.name}`} onClick={() => leaveGuarded(() => setEditingId(s.id))}>
                        Edytuj
                      </button>
                    )}
                    <RowMenu label={`Więcej działań: ${s.name}`} actions={more} />
                  </td>
                </tr>
                {editingId === s.id && (
                  <tr className="row-editor">
                    <td colSpan={columns}>
                      <ServiceForm service={s} showPrice={showPrice} onClose={() => setEditingId(null)} />
                    </td>
                  </tr>
                )}
                {historyId === s.id && seeHistory && (
                  <tr>
                    <td colSpan={columns}>
                      <HistoryPanel entityId={s.id} resource={SERVICES_RESOURCE} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ServicesPage() {
  const { me } = useMe();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Service | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const list = useQuery({ queryKey: [...PRICING_KEY, 'services'], queryFn: () => api<{ services: Service[] }>('/api/pricing/services') });
  if (!me) return null;
  const services = list.data?.services ?? [];
  // What the role may see comes with the session (`hiddenFields` of /api/auth/me), also for an empty list.
  const showPrice = fieldVisible(me, SERVICES_RESOURCE, 'priceGrosze');
  // owner 2026-10-24: every crew function is a service — group „Obsada” (only when the role sees the link)
  const showFunction = fieldVisible(me, SERVICES_RESOURCE, 'functionId');
  const crew = services.filter((s) => !!s.functionId);
  const others = services.filter((s) => !s.functionId);
  const table = { me, showPrice, showFunction, editingId, setEditingId, setDeleting, historyId, setHistoryId };

  return (
    <section className="page" data-feature-id="pricing.services">
      <div className="page-head">
        <h2>Cenniki</h2>
        {can(me, SERVICES_RESOURCE, 'create') && !creating && editingId === null && (
          <button className="btn primary" type="button" onClick={() => leaveGuarded(() => setCreating(true))}>
            Dodaj usługę
          </button>
        )}
      </div>
      <PricingNav me={me} />
      <p className="muted">Usługi to pozycje kosztorysu spoza sprzętu: obsada (realizator, technik…), transport i inne. Cena jest za dzień; w kosztorysie mnoży się przez ilość i liczbę dni.</p>

      {creating && <ServiceForm showPrice={showPrice} onClose={() => setCreating(false)} />}

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać usług'}</p>}
      {list.data && services.length === 0 && <p className="muted">Brak usług w cenniku.</p>}
      {crew.length > 0 && (
        <div data-feature-id="pricing.crew_services">
          <h3>Obsada</h3>
          <p className="muted">
            Każda funkcja z{' '}
            {can(me, FUNCTIONS_RESOURCE, 'view') ? <Link to="/osoby/funkcje">Osoby → Funkcje</Link> : '„Osoby → Funkcje”'} jest tu usługą. Nową funkcję dodajesz tam; tutaj ustawiasz jej cenę dla klienta. Nazwę zmienisz w obu miejscach, usuwa się ją razem z funkcją.
          </p>
          <ServicesTable label="Obsada" services={crew} crew {...table} />
        </div>
      )}
      {others.length > 0 && (
        <>
          {crew.length > 0 && <h3>Pozostałe usługi</h3>}
          <ServicesTable label="Usługi" services={others} crew={false} {...table} />
        </>
      )}
      {deleting && <DeleteDialog title="Usunąć usługę?" name={deleting.name} url={`/api/pricing/services/${deleting.id}?version=${deleting.version}`} onClose={() => setDeleting(null)} />}
    </section>
  );
}
